import { type } from 'arktype';
import { createAddressSchema, type AddressResponse } from './address.model';

import type { TranslatableText } from '@zag/core/common';

import type { CustomPropertiesData } from './custom-property.model';
import { translatableTextSchema } from './translatable-text.model';

/**
 * Field names that are translatable on a location entity.
 */
export const LOCATION_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type LocationTranslatableField = (typeof LOCATION_TRANSLATABLE_FIELDS)[number];
export type LocationTranslations = TranslatableText<LocationTranslatableField>;

export const locationQuerySchema = type({
  'name?': 'string',
  'type?': "'physical' | 'virtual'",
});

export type LocationQuery = typeof locationQuerySchema.t;

export const createLocationSchema = type({
  type: type("'physical' | 'virtual'").configure({
    description: 'Whether this location has a physical address (warehouse, office) or is a virtual workspace (remote, hybrid).',
  }),
  'name?': type('string').configure({
    description: 'Display name shown in the UI and in assignment pickers. Optional if `translations.en.name` is supplied.',
    examples: ['Zurich HQ'],
  }),
  'description?': type('string | null').configure({
    description: 'Free-form description of the location. `null` clears the existing value.',
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier used in location chips. `null` falls back to the type icon.',
    examples: ['business'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS color used as the accent for this location. Six-digit hex recommended.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({ description: 'URL to a representative image of the location.' }),
  'data?': type('object | null').configure({
    description:
      'Custom values go under `data.customProperties`, a record keyed by the custom-property definition `key`. Values are validated against the tenant `location` definitions. `null` clears them.',
    examples: [{ customProperties: { region: 'emea', floor: 3, isAccessible: true } }],
  }),
  'addressId?': type('string | null').configure({ description: 'Reference to an address entity for physical locations.' }),
  'address?': createAddressSchema.or(type('null')).configure({
    description:
      'Inline structured postal address for physical locations. When supplied, the API creates or updates the linked address record.',
  }),
  'url?': type('string | null').configure({
    description: 'Virtual-meeting URL (Zoom, Teams, etc.) for virtual locations.',
    examples: ['https://meet.example.com/daily-standup'],
  }),
  'translations?': translatableTextSchema(LOCATION_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({
  description: 'Payload for creating a new location.',
  examples: [
    {
      name: 'Pflegeheim Zürichberg',
      type: 'physical',
      data: { customProperties: { region: 'emea', floor: 3 } },
      translations: { en: { name: 'Zurich Care Home' }, de: { name: 'Pflegeheim Zürichberg' } },
    },
  ],
});

export type CreateLocation = typeof createLocationSchema.t;

export const updateLocationSchema = type({
  'type?': "'physical' | 'virtual'",
  'name?': type('string').configure({ examples: ['Zurich HQ'] }),
  'description?': 'string | null',
  'icon?': 'string | null',
  'color?': 'string | null',
  'imageUrl?': 'string | null',
  'data?': type('object | null').configure({
    description:
      'Updated custom values go under `data.customProperties`, a record keyed by the custom-property definition `key`. Values are validated against the tenant `location` definitions. `null` clears them.',
    examples: [{ customProperties: { region: 'apac', floor: 2 } }],
  }),
  'addressId?': 'string | null',
  'address?': createAddressSchema.or(type('null')),
  'url?': 'string | null',
  'translations?': translatableTextSchema(LOCATION_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({
  description: 'Partial update of a location — fields omitted from the body remain unchanged.',
  examples: [{ data: { customProperties: { region: 'apac' } }, translations: { de: { name: 'Pflegeheim Zürichberg' } } }],
});

export type UpdateLocation = typeof updateLocationSchema.t;

/**
 * Location response type returned by the API.
 */
export interface LocationResponse {
  id: string;
  type: 'physical' | 'virtual';
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
  addressId: string | null;
  address?: AddressResponse | null;
  url: string | null;
  employeeCount?: number;
  data: CustomPropertiesData;
  /**
   * Full per-language translations map. Only populated when the caller asked
   * for it via `?include=translations` on detail-edit fetches.
   */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
